/* eslint-disable @typescript-eslint/no-explicit-any */
import type { DependencyList, EffectCallback } from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { DeviceInformation } from "@gaming/device-information";

import { getRequestInfo, isReactDomHydrating } from "../redux";
import { debounce } from "./debounce";

// Based on MWF ViewPort breakpoints
export enum ViewPort {
  XXS,
  XS,
  SM,
  MD,
  LG,
  XL,
}

export const viewPortMaxSize = {
  [ViewPort.XXS]: 400,
  [ViewPort.XS]: 540,
  [ViewPort.SM]: 860,
  [ViewPort.MD]: 1084,
  [ViewPort.LG]: 1400,
  [ViewPort.XL]: Number.MAX_SAFE_INTEGER,
};

const snapPoints = [
  { minWidth: 0, viewPort: ViewPort.XXS },
  { minWidth: 400, viewPort: ViewPort.XS },
  { minWidth: 540, viewPort: ViewPort.SM },
  { minWidth: 860, viewPort: ViewPort.MD },
  { minWidth: 1084, viewPort: ViewPort.LG },
  { minWidth: 1400, viewPort: ViewPort.XL },
];

const defaultWidth = viewPortMaxSize[ViewPort.LG];

const getWidth = () => document.documentElement.clientWidth || document.body.clientWidth;

const usePostHydrationEffect = (effect: EffectCallback, deps: DependencyList) => {
  const isReactHydrating = useSelector(isReactDomHydrating);

  useEffect(() => {
    if (!isReactHydrating) {
      return effect();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, isReactHydrating]);
};

const useInitialWidth = () => {
  const isReactHydrating = useSelector(isReactDomHydrating);
  const userAgentString = useSelector(getRequestInfo).userAgent;

  const deviceInformation = useMemo(
    () => new DeviceInformation(userAgentString),
    [userAgentString]
  );

  // ensure width during react hydration is same as what's detected on the
  // server, to ensure react doesn't blow up trying to reconcile differences
  // between client & server render. Instead, update with actual width on mount,
  // and trigger a re-render
  if (typeof window !== "undefined" && !isReactHydrating) {
    return getWidth();
  }

  if (!userAgentString) {
    return defaultWidth;
  }

  // Estimate a window width based on user agent. While not ideal, there is a
  // high chance, that this would result in the closest ViewPort matching the
  // browser width on client
  if (deviceInformation.isMobile) {
    return viewPortMaxSize[ViewPort.XS];
  }

  if (deviceInformation.isTablet) {
    return viewPortMaxSize[ViewPort.MD];
  }

  return defaultWidth;
};

export const useCurrentWidth = () => {
  const initialWidth = useInitialWidth();
  const [width, setWidth] = useState(initialWidth);

  usePostHydrationEffect(() => {
    const resizeListener = debounce(() => {
      setWidth(getWidth());
    }, 150);

    // set correct width once after hydration and attach resize listener
    setWidth(getWidth());
    window.addEventListener("resize", resizeListener);

    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  return width;
};

export const useCurrentViewPort = () => {
  const width = useCurrentWidth();

  let newViewPort = ViewPort.XL;
  for (const snap of snapPoints) {
    if (width >= snap.minWidth) {
      newViewPort = snap.viewPort;
    } else {
      break;
    }
  }

  return newViewPort;
};

// This hooks allows to detect whether an element is on screen together with a margin of how much of
// the element should be visible before considering it as on screen.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useOnScreen = (ref: any, rootMargin = "0px"): boolean | null => {
  // State and setter for storing whether element is visible
  // null indicates unknown
  const [isIntersecting, setIntersecting] = useState<boolean | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(entry.isIntersecting);
      },
      {
        rootMargin,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    const current = ref.current;
    return () => {
      observer.unobserve(current);
    };
  }); // Empty array ensures that effect is only run on mount and un-mount

  return isIntersecting;
};
